// src/services/postsService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc,
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  increment,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { firestoreService } from './firestoreService';

class PostsService {
  
  // Get all posts for a book
  async getPosts(bookId, options = {}) {
    try {
      const {
        limit: queryLimit,
        visibility,
        status,
        category,
        author
      } = options;

      let q = query(
        collection(db, 'pb-books', bookId, 'pb-posts'),
        orderBy('publishDate', 'desc')
      );

      // Apply filters
      if (visibility) {
        q = query(q, where('visibility', '==', visibility));
      }
      
      if (status) {
        q = query(q, where('status', '==', status));
      }
      
      if (category) {
        q = query(q, where('category', '==', category));
      }
      
      if (author) {
        q = query(q, where('authorId', '==', author));
      }

      if (queryLimit) {
        q = query(q, limit(queryLimit));
      }

      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
        publishDate: doc.data().publishDate?.toDate?.() || new Date()
      }));
    } catch (error) {
      console.error('Error getting posts:', error);
      throw error;
    }
  }

  // Get a single post
  async getPost(bookId, postId) {
    try {
      const postRef = doc(db, 'pb-books', bookId, 'pb-posts', postId);
      const postSnap = await getDoc(postRef);
      
      if (postSnap.exists()) {
        return {
          id: postSnap.id,
          ...postSnap.data(),
          createdAt: postSnap.data().createdAt?.toDate?.() || new Date(),
          updatedAt: postSnap.data().updatedAt?.toDate?.() || new Date()
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting post:', error);
      throw error;
    }
  }

  // Create a new post
  async createPost(bookId, postData) {
    try {
      const postsRef = collection(db, 'pb-books', bookId, 'pb-posts');
      
      const post = {
        ...postData,
        bookId,
        views: 0,
        likes: 0,
        comments: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        publishDate: postData.publishDate ? new Date(postData.publishDate) : serverTimestamp()
      };

      const docRef = await addDoc(postsRef, post);
      
      // Send notifications if enabled
      if (postData.sendNotification && postData.status === 'published') {
        await this.sendPostNotification(bookId, docRef.id, post);
      }
      
      return {
        id: docRef.id,
        ...post
      };
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  }

  // Update a post
  async updatePost(bookId, postId, updates) {
    try {
      const postRef = doc(db, 'pb-books', bookId, 'pb-posts', postId);
      
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };

      // Handle publish date
      if (updates.publishDate) {
        updateData.publishDate = new Date(updates.publishDate);
      }

      await updateDoc(postRef, updateData);
      
      // Send notifications if status changed to published and notifications enabled
      if (updates.status === 'published' && updates.sendNotification) {
        await this.sendPostNotification(bookId, postId, updates);
      }
      
      return updateData;
    } catch (error) {
      console.error('Error updating post:', error);
      throw error;
    }
  }

  // Update post status
  async updatePostStatus(bookId, postId, status) {
    try {
      const postRef = doc(db, 'pb-books', bookId, 'pb-posts', postId);
      
      await updateDoc(postRef, {
        status,
        updatedAt: serverTimestamp(),
        ...(status === 'published' && { publishDate: serverTimestamp() })
      });
      
      return true;
    } catch (error) {
      console.error('Error updating post status:', error);
      throw error;
    }
  }

  // Delete a post
  async deletePost(bookId, postId) {
    try {
      const postRef = doc(db, 'pb-books', bookId, 'pb-posts', postId);
      await deleteDoc(postRef);
      
      // Also delete associated comments, likes, etc.
      await this.deletePostEngagement(bookId, postId);
      
      return true;
    } catch (error) {
      console.error('Error deleting post:', error);
      throw error;
    }
  }

  // Increment post views
  async incrementViews(bookId, postId) {
    try {
      const postRef = doc(db, 'pb-books', bookId, 'pb-posts', postId);
      await updateDoc(postRef, {
        views: increment(1),
        lastViewedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error incrementing views:', error);
    }
  }

  // Like/Unlike post
  async toggleLike(bookId, postId, userId) {
    try {
      const likeRef = doc(db, 'pb-books', bookId, 'pb-posts', postId, 'pb-likes', userId);
      const likeSnap = await getDoc(likeRef);
      
      const postRef = doc(db, 'pb-books', bookId, 'pb-posts', postId);
      
      if (likeSnap.exists()) {
        // Unlike
        await deleteDoc(likeRef);
        await updateDoc(postRef, {
          likes: increment(-1)
        });
        return false;
      } else {
        // Like
        await addDoc(collection(db, 'pb-books', bookId, 'pb-posts', postId, 'pb-likes'), {
          userId,
          createdAt: serverTimestamp()
        });
        await updateDoc(postRef, {
          likes: increment(1)
        });
        return true;
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      throw error;
    }
  }

  // Add comment to post
  async addComment(bookId, postId, commentData) {
    try {
      const commentsRef = collection(db, 'pb-books', bookId, 'pb-posts', postId, 'pb-comments');
      
      const comment = {
        ...commentData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(commentsRef, comment);
      
      // Update post comment count
      const postRef = doc(db, 'pb-books', bookId, 'pb-posts', postId);
      await updateDoc(postRef, {
        comments: increment(1)
      });
      
      return {
        id: docRef.id,
        ...comment
      };
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  }

  // Get post comments
  async getComments(bookId, postId, options = {}) {
    try {
      const { limit: queryLimit = 50 } = options;
      
      let q = query(
        collection(db, 'pb-books', bookId, 'pb-posts', postId, 'pb-comments'),
        orderBy('createdAt', 'desc')
      );

      if (queryLimit) {
        q = query(q, limit(queryLimit));
      }

      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
    } catch (error) {
      console.error('Error getting comments:', error);
      throw error;
    }
  }

  // Send post notification
  async sendPostNotification(bookId, postId, postData) {
    try {
      // Implementation depends on your notification service
      // This is a placeholder for the notification logic
      console.log('Sending post notification:', { bookId, postId, postData });
      
      // Example: Send to notification service
      // await notificationService.createPostNotification(bookId, {
      //   postId,
      //   title: postData.title,
      //   visibility: postData.visibility,
      //   priority: postData.priority || 'normal'
      // });
    } catch (error) {
      console.error('Error sending post notification:', error);
    }
  }

  // Delete post engagement data
  async deletePostEngagement(bookId, postId) {
    try {
      const batch = writeBatch(db);
      
      // Delete likes
      const likesQuery = query(collection(db, 'pb-books', bookId, 'pb-posts', postId, 'pb-likes'));
      const likesSnapshot = await getDocs(likesQuery);
      
      likesSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Delete comments
      const commentsQuery = query(collection(db, 'pb-books', bookId, 'pb-posts', postId, 'pb-comments'));
      const commentsSnapshot = await getDocs(commentsQuery);
      
      commentsSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error deleting post engagement:', error);
    }
  }

  // Bulk operations
  async bulkUpdateStatus(bookId, postIds, status) {
    try {
      const batch = writeBatch(db);
      
      postIds.forEach(postId => {
        const postRef = doc(db, 'pb-books', bookId, 'pb-posts', postId);
        batch.update(postRef, {
          status,
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error bulk updating post status:', error);
      throw error;
    }
  }

  // Archive expired posts
  async archiveExpiredPosts(bookId) {
    try {
      const now = new Date();
      const expiredQuery = query(
        collection(db, 'pb-books', bookId, 'pb-posts'),
        where('expiryDate', '<=', now),
        where('status', '!=', 'archived')
      );
      
      const snapshot = await getDocs(expiredQuery);
      
      if (!snapshot.empty) {
        const batch = writeBatch(db);
        
        snapshot.docs.forEach(doc => {
          batch.update(doc.ref, {
            status: 'archived',
            updatedAt: serverTimestamp()
          });
        });
        
        await batch.commit();
        
        return snapshot.docs.length;
      }
      
      return 0;
    } catch (error) {
      console.error('Error archiving expired posts:', error);
      throw error;
    }
  }

  // Upload post attachments
  async uploadAttachment(file, bookId, postId) {
    try {
      const path = `pb-${bookId}/posts/${postId}/attachments`;
      return await firestoreService.uploadFile(file, path);
    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw error;
    }
  }

  // Get post analytics
  async getPostAnalytics(bookId, postId) {
    try {
      const post = await this.getPost(bookId, postId);
      if (!post) return null;

      const comments = await this.getComments(bookId, postId);
      const likesSnapshot = await getDocs(collection(db, 'pb-books', bookId, 'pb-posts', postId, 'pb-likes'));
      
      return {
        ...post,
        totalComments: comments.length,
        totalLikes: likesSnapshot.docs.length,
        engagementRate: post.views > 0 ? ((comments.length + likesSnapshot.docs.length) / post.views * 100).toFixed(2) : 0
      };
    } catch (error) {
      console.error('Error getting post analytics:', error);
      throw error;
    }
  }
}

export const postsService = new PostsService();